/* eslint-disable import/no-cycle */
import { CommonModule } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  OnDestroy,
  OnInit,
  Signal,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
  signal,
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { FormControl, FormGroup, ValidationErrors } from '@angular/forms'

import { isEqual as _isEqual, isNil as _isNil, set as _set } from 'lodash/fp'

import {
  EMPTY,
  Observable,
  combineLatest,
  debounce,
  distinctUntilChanged,
  filter,
  from,
  map,
  startWith,
  switchMap,
  tap,
  timer,
} from 'rxjs'

import { FlxWrapperStyleDirective } from '@flowx/angular-theme'
import {
  CurrencyObject,
  DataModelType,
  FormFieldTemplateConfig,
  FormFieldsEnum,
  ProcessActionTriggerEvent,
  ProcessDataValue,
  ResolvedAction,
  ValidatorType,
  getActionsByTemplateConfigAndTrigger$,
  getCurrencyValue,
  getCurrentReadOnlyState,
  getProcessDataValueFromPathSync,
  isGateHolding,
  isGateHolding$,
  isOfDataType,
  isUiCallAction,
  processDataValueFromPath$,
  prototypeInstanceMetaToPath,
  updateProcessData,
  updateProcessDataAtPath,
} from '@flowx/core-sdk'

import { FlxActionService } from '../../services/flx-action.service'
import { FlxFormsService } from '../../services/flx-forms.service'
import { isDisplayOnlyControlWrite, isLateControlWrite } from '../../utils/forms'
import {
  buildAsyncValidators,
  buildFieldValidators,
  validatorParamChanges$,
} from '../../utils/validators'
import { COMPONENT_WRAPPERS_MAP } from '../component.dictionaries'
import { getBoolExpressionSignal } from '../helpers'

@Component({
  selector: 'flx-form-field',
  imports: [CommonModule],
  templateUrl: './flx-form-field.component.html',
  styleUrl: './flx-form-field.component.scss',
  host: { class: 'flx-form-field' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxFormFieldComponent implements OnInit, OnDestroy, AfterViewInit {
  #cdr = inject(ChangeDetectorRef)
  #destroyRef = inject(DestroyRef)
  #flxFormService = inject(FlxFormsService)
  #actionService = inject(FlxActionService)
  #injector = inject(Injector)
  #elementRef = inject(ElementRef)
  #flxWrapperStyle = inject(FlxWrapperStyleDirective)

  #suppressProcessDataEventEmission = false
  #suppressFormControlEventEmission = false

  // actions for the CHANGE trigger – kept in sync with the process-actions store
  // so they arrive even when the node's details resolve after this component mounts.
  #changeActions = signal<ResolvedAction[]>([])

  // inputs
  fieldConfig = input.required<FormFieldTemplateConfig>()
  formGroup = input.required<FormGroup>()
  validateOn = input.required<string>()
  formSubmitted = input.required<Observable<boolean>>()
  parentFormDisabled = input.required<boolean>()

  readonly COMPONENTS_MAP = COMPONENT_WRAPPERS_MAP

  formControl = computed(() => this.formGroup().get(this.fieldConfig().flowxUuid) as FormControl)
  formFieldSubmitted = computed(() =>
    this.#flxFormService.formSubmitted$(this.fieldConfig().flowxUuid)
  )
  displayOptions = computed(() => this.fieldConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)
  disabledState = computed(() => {
    if (this.readonly()) {
      return !this.hasChangeAction()
    }
    const isDisabled = this.isDisabled()
    const parentFormDisabled = this.parentFormDisabled()
    const isHidden = this.isHidden()
    return isDisabled || parentFormDisabled || isHidden
  })
  fieldKey = computed(() => {
    const { key, prototypeInstanceMeta } = this.fieldConfig()
    return prototypeInstanceMeta
      ? `${prototypeInstanceMetaToPath(prototypeInstanceMeta)}.${key}`
      : key
  })
  readonly = getCurrentReadOnlyState

  errors = signal<ValidationErrors>({})

  showErrors$: Observable<boolean>

  // expressions
  isHidden: Signal<boolean>
  isDisabled: Signal<boolean>

  // true when a uiCall CHANGE action is present (drives readonly/disabled state)
  hasChangeAction = computed(() =>
    this.#changeActions().some(({ action }) => isUiCallAction(action))
  )

  constructor() {
    // disable control
    effect(() => {
      const isDisabled = this.disabledState()
      const isHidden = this.isHidden()
      const isParentFormDisabled = this.parentFormDisabled()
      this.#toggleDisableControl(isDisabled || isHidden || isParentFormDisabled)
    })
  }
  ngOnDestroy(): void {
    // clear validators and errors when the form field is destroyed
    this.formControl().clearValidators()
    this.formControl().clearAsyncValidators()
    this.errors.set(null)
    this.formControl().updateValueAndValidity({ emitEvent: false })
    this.#flxFormService.removeFromValidation(this.fieldConfig().flowxUuid)
  }

  ngOnInit(): void {
    this.showErrors$ = combineLatest({
      formSubmitted: this.formSubmitted(),
      formFieldSubmitted: this.formFieldSubmitted(),
      // control events rather than valueChanges: programmatic pristine resets
      // (e.g. after CHANGE actions run) don't emit a value but must re-evaluate the guard
      controlEvents: this.formControl().events.pipe(startWith(null)),
    }).pipe(
      map(
        ({ formSubmitted, formFieldSubmitted }) =>
          (this.validateOn() === 'blur' && this.formControl().dirty) ||
          !!formSubmitted ||
          !!formFieldSubmitted
      ),
      distinctUntilChanged()
    )

    const hasAsyncValidators =
      this.fieldConfig().validators?.filter(({ type }) => type === ValidatorType.ASYNC).length > 0
    runInInjectionContext(this.#injector, () => {
      const { hide, disabled } = this.flowxProps()
      const hiddenByExpression = getBoolExpressionSignal(hide, this.fieldConfig(), {
        logType: 'hide',
      })
      // A field the Readiness gate holds is hidden like any other: no control, no layout space.
      const { processInstanceUuid, key } = this.fieldConfig()
      const gateHolding = toSignal(isGateHolding$(processInstanceUuid, key), {
        initialValue: isGateHolding(processInstanceUuid, key),
      })
      this.isHidden = computed(() => hiddenByExpression() || gateHolding())
      this.isDisabled = getBoolExpressionSignal(disabled, this.fieldConfig(), {
        logType: 'disabled',
      })
    })

    // FORM CONTROL updates => update PROCESS DATA
    this.formControl()
      .valueChanges.pipe(
        filter(() => !isDisplayOnlyControlWrite()),
        // Read the late mark while setValue is still on the stack: it travels with the value.
        map((value) => ({ value, late: isLateControlWrite() })),
        filter(() => !this.disabledState()),
        filter(() => {
          if (this.#suppressFormControlEventEmission) {
            this.#suppressFormControlEventEmission = false
            return false
          }
          return true
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(({ value, late }) => this.#setProcessDataValue(value, this.fieldConfig(), late))

    // PROCESS DATA updates => update FORM CONTROL
    processDataValueFromPath$(
      this.fieldConfig().processInstanceUuid,
      this.fieldKey(),
      hasAsyncValidators
    )
      .pipe(
        filter(() => {
          if (this.#suppressProcessDataEventEmission) {
            this.#suppressProcessDataEventEmission = false
            return false
          }
          return true
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((value) => {
        this.#reconcileFormControl(value, this.fieldConfig())

        // this is needed to trigger change detection on child form fields; because these changes from the model come async and we use on push,
        // there is no way for the child element to know of this change, unless we change one of it's inputs. markForCheck does not help since it marks THIS component
        // and ancestors as dirty, not children
        this.errors.set(this.formControl().errors)

        // FORCE STATUS CHANGE FOR ASYNC VALIDATION
        if (hasAsyncValidators) {
          this.formControl().updateValueAndValidity()
        } else {
          this.formControl().updateValueAndValidity({ emitEvent: false })
        }
      })

    // Data a validator param reads moved: re-validate without user input.
    validatorParamChanges$(this.fieldConfig())
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.formControl().updateValueAndValidity())

    this.formControl()
      .statusChanges.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        // during the pending state, the form controls errors are unset
        // we don't want to set errors when the form control is pending -> while running async validators -> the errors should be set after the validation is complete
        if (this.formControl().status === 'PENDING') {
          return
        }

        this.errors.set(this.formControl().errors)
      })

    const isSelect = this.fieldConfig().componentIdentifier === FormFieldsEnum.SELECT

    const change$ = this.formControl().valueChanges.pipe(
      filter(() => !this.disabledState()),
      debounce(() => timer(isSelect ? 0 : 800)), // no debounce for select, debounce for other fields
      filter(() => this.formControl().dirty)
    )

    // SETUP CHANGE TRIGGER
    // Keep the resolved CHANGE actions in sync with the store so they're picked up
    // even when the node's details resolve after this component mounts.
    getActionsByTemplateConfigAndTrigger$(this.fieldConfig(), ProcessActionTriggerEvent.CHANGE)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((actions) => this.#changeActions.set(actions))

    // Always subscribe; gate on the current action set at emit time rather than at
    // setup time, so late-arriving actions still fire on subsequent value changes.
    change$
      .pipe(
        switchMap(() => {
          const changeActions = this.#changeActions()
          if (changeActions.length === 0) {
            return EMPTY
          }
          // Mark pristine/untouched BEFORE the orchestrator runs so the cascading
          // process-data → form-control re-emission is filtered out by change$'s `dirty` gate.
          this.formControl().markAsPristine({ onlySelf: true })
          this.formControl().markAsUntouched({ onlySelf: true })
          return from(this.#actionService.runActions(changeActions, this.fieldConfig())).pipe(
            tap(() => {
              const uiCallAction = changeActions.find(({ action }) =>
                isUiCallAction(action)
              )?.action
              const componentIdentifier = this.fieldConfig().componentIdentifier
              const isInputOrTextarea = [FormFieldsEnum.INPUT, FormFieldsEnum.TEXTAREA].includes(
                componentIdentifier
              )
              if (isInputOrTextarea && uiCallAction?.params?.blocksUi) {
                const inputElement = this.#elementRef.nativeElement.querySelector('input, textarea')
                inputElement?.blur()
              }
            })
          )
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe()
  }

  ngAfterViewInit(): void {
    runInInjectionContext(this.#injector, () => {
      effect(() => {
        this.#flxWrapperStyle.toggleDisplay(this.isHidden())
      })
    })
  }

  #getProcessDataValue(templateConfig: FormFieldTemplateConfig): ProcessDataValue {
    const { processInstanceUuid } = templateConfig
    return getProcessDataValueFromPathSync(processInstanceUuid, this.fieldKey())
  }

  #setProcessDataValue(
    value: unknown,
    templateConfig: FormFieldTemplateConfig,
    late: boolean
  ): void {
    const processDataValue = this.#mapFormControlValueToProcessDataValue(value, templateConfig)
    const currentProcessDataValue = this.#getProcessDataValue(templateConfig)

    // processDataValueFromPath$ won't emit the same value multiple times unless alwaysEmit is true
    // suppressProcessDataEventEmission should not be set to true unless the emission will occur
    if (!_isEqual(processDataValue, currentProcessDataValue)) {
      if (templateConfig.prototypeInstanceMeta) {
        // form in collections -> we update the value at the path of the collection which is an index of an array
        const newValue = _set(templateConfig.key, processDataValue, {})
        const path = prototypeInstanceMetaToPath(templateConfig.prototypeInstanceMeta)
        updateProcessDataAtPath(templateConfig.processInstanceUuid, path, newValue, {
          userInput: !late,
          late,
        })
      } else {
        const newValue = _set(templateConfig.key, processDataValue, {})
        this.#suppressProcessDataEventEmission = true
        updateProcessData(templateConfig.processInstanceUuid, newValue, { userInput: !late })
      }
      // The store echoes synchronously, so the latch is spent or the write moved nothing. Core can
      // settle the write (a Slider sharing the key clamps it): show what the store holds.
      this.#suppressProcessDataEventEmission = false
      this.#reconcileFormControl(this.#getProcessDataValue(templateConfig), templateConfig)
    }
  }

  #reconcileFormControl(value: ProcessDataValue, templateConfig: FormFieldTemplateConfig): void {
    const formControlValue = this.formControl().value?.value || this.formControl().value
    const formControlValueFromProcessDataValue = this.#mapProcessDataValueToFormControlValue(
      value,
      templateConfig
    )
    const parsedFormControlValue = this.#parseValue(formControlValue, templateConfig)
    const parsedFormControlValueFromProcessDataValue = this.#parseValue(
      formControlValueFromProcessDataValue,
      templateConfig
    )

    if (!_isEqual(parsedFormControlValue, parsedFormControlValueFromProcessDataValue)) {
      this.#setFormControlValue(formControlValueFromProcessDataValue, templateConfig)
    }
  }

  #setFormControlValue(value: unknown, templateConfig: FormFieldTemplateConfig): void {
    const parsedValue = this.#parseValue(value, templateConfig)
    const formControlValue = this.#mapValueToFormControlValue(parsedValue, templateConfig)
    this.#suppressFormControlEventEmission = true
    this.formControl().setValue(formControlValue, { onlySelf: true })
  }

  #toggleDisableControl(isDisabled: boolean): void {
    if (isDisabled && !this.formControl().disabled) {
      this.#suppressFormControlEventEmission = true
      this.formControl().disable()
      this.formControl().clearValidators()
      this.formControl().clearAsyncValidators()
      this.errors.set(this.formControl().errors)
    } else if (!isDisabled && this.formControl().disabled) {
      this.formControl().setAsyncValidators(buildAsyncValidators(this.fieldConfig()))
      this.formControl().setValidators(buildFieldValidators(this.fieldConfig()))
      this.#suppressFormControlEventEmission = true
      this.formControl().enable()
      this.errors.set(this.formControl().errors)
    }
  }

  #parseValue(value: any, templateConfig: FormFieldTemplateConfig): ProcessDataValue {
    if (_isNil(value) || value === '') {
      return null
    }

    const componentIdentifier = templateConfig.componentIdentifier
    if (componentIdentifier === FormFieldsEnum.SLIDER) {
      return this.#parseInt(value)
    }

    if (isOfDataType(DataModelType.INTEGER, templateConfig)) {
      return this.#parseInt(value)
    }
    if (
      isOfDataType(DataModelType.FLOAT, templateConfig) ||
      isOfDataType(DataModelType.CURRENCY, templateConfig)
    ) {
      return this.#parseFloat(value)
    }

    const inputType =
      'inputType' in templateConfig.displayOptions.flowxProps
        ? templateConfig.displayOptions.flowxProps.inputType
        : undefined
    if (inputType === 'number') {
      return this.#parseFloat(value)
    }

    if (componentIdentifier === FormFieldsEnum.MULTI_SELECT) {
      return Array.isArray(value) ? value : [value]
    }

    return value
  }

  #parseInt(value: any): any {
    const parsedValue = parseInt(value, 10)
    return Number.isFinite(parsedValue) ? parsedValue : value
  }

  #parseFloat(value: any): any {
    const parsedValue = parseFloat(value)
    return Number.isFinite(parsedValue) ? parsedValue : value
  }

  #mapFormControlValueToProcessDataValue(
    value: any,
    templateConfig: FormFieldTemplateConfig
  ): ProcessDataValue {
    const newValue = value?.value ?? value
    const parsedValue = this.#parseValue(newValue, templateConfig)

    if (isOfDataType(DataModelType.CURRENCY, templateConfig)) {
      return {
        amount: parsedValue,
      }
    }
    return parsedValue
  }

  #mapProcessDataValueToFormControlValue(
    processDataValue: ProcessDataValue,
    templateConfig: FormFieldTemplateConfig
  ): any {
    let value = processDataValue

    if (isOfDataType(DataModelType.CURRENCY, templateConfig)) {
      const currencyValue = getCurrencyValue(value as CurrencyObject)
      value = typeof currencyValue === 'object' ? currencyValue.amount : currencyValue
    }

    return this.#mapValueToFormControlValue(value, templateConfig)
  }

  #mapValueToFormControlValue(value: any, templateConfig: FormFieldTemplateConfig): any {
    const { componentIdentifier } = templateConfig

    // we use masked inputs, which require the form control value to be string
    if (componentIdentifier === FormFieldsEnum.INPUT) {
      return value?.toString() || null
    }
    return value
  }
}
